import { t } from 'i18next';
import type { KeyboardEvent } from 'react';
import { cn } from '../../../lib/styles';
import { Badge } from '../badge/badge.spark';
import { TabsFrame, TabsPanel, useTabIds } from './tabs.shared';
import type { TabsProps } from './types';

type TabIds = ReturnType<typeof useTabIds>;

export function Tabs<T extends string = string>({
  tabs,
  value,
  onChange,
  showNav = true,
  ariaLabel,
  children,
}: TabsProps<T>) {
  const ids = useTabIds();

  return (
    <TabsFrame
      value={value}
      hasStrip={showNav}
      panelAttributes={(panelValue) => ({
        id: ids.panelId(panelValue),
        role: 'tabpanel',
        // Without a strip there is no tab element left to name the panel.
        'aria-labelledby': showNav ? ids.tabId(panelValue) : undefined,
      })}
    >
      {showNav && (
        <TabStrip tabs={tabs} value={value} onChange={onChange} ariaLabel={ariaLabel} ids={ids} />
      )}
      {children}
    </TabsFrame>
  );
}

function TabStrip<T extends string>({
  tabs,
  value,
  onChange,
  ariaLabel,
  ids,
}: Pick<TabsProps<T>, 'tabs' | 'value' | 'onChange' | 'ariaLabel'> & { ids: TabIds }) {
  // Arrow/Home/End walk the strip, matching the keyboard behaviour consumers get for
  // free from the Polaris and Wix tabs. Selection follows focus, so the panel keeps up.
  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    const index = destinationIndex(
      event.key,
      tabs.findIndex((tab) => tab.value === value),
      tabs.length,
    );
    const tab = index === undefined ? undefined : tabs[index];
    if (!tab) {
      return;
    }

    event.preventDefault();
    onChange(tab.value);
    document.getElementById(ids.tabId(tab.value))?.focus();
  }

  return (
    // A nav landmark would compete with the tablist role, so this stays a plain div.
    <div
      role="tablist"
      aria-label={ariaLabel ?? t('aria:tabs')}
      onKeyDown={handleKeyDown}
      className="flex gap-1 overflow-x-auto border-spark-border-default border-b"
    >
      {tabs.map((tab) => {
        const isActive = tab.value === value;

        return (
          <button
            key={tab.value}
            type="button"
            role="tab"
            id={ids.tabId(tab.value)}
            aria-selected={isActive}
            aria-controls={ids.panelId(tab.value)}
            // Roving tabindex: the strip is a single tab stop, arrow keys move within it.
            tabIndex={isActive ? 0 : -1}
            onClick={() => onChange(tab.value)}
            className={cn(
              // `readonly-allow` keeps tabs switchable in forms' read-only mode.
              // The ring is inset because the strip scrolls, and an outset one is clipped
              // along the bottom border.
              'readonly-allow shrink-0 cursor-pointer whitespace-nowrap rounded-t-lg border-b-2 px-3 py-2 font-medium text-sm transition-colors focus:outline-hidden focus-visible:inset-ring-2 focus-visible:inset-ring-platform-primary',
              isActive
                ? 'border-platform-primary text-platform-primary'
                : 'border-transparent text-spark-text-secondary hover:bg-gray-50 hover:text-spark-text-primary',
            )}
          >
            <span className="flex items-center gap-1.5">
              {tab.label}
              {tab.badge && <Badge size="sm">{tab.badge}</Badge>}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function destinationIndex(key: string, current: number, count: number) {
  switch (key) {
    case 'ArrowRight':
      return (current + 1) % count;
    case 'ArrowLeft':
      return (current - 1 + count) % count;
    case 'Home':
      return 0;
    case 'End':
      return count - 1;
    default:
      return undefined;
  }
}

Tabs.Panel = TabsPanel;
